// src/components/dictionaries/AddPrzeznaczenie.js
import React, { useState, useContext, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { useMediaQuery } from "react-responsive";
import { baseURL } from "../../utils/api";

const AddPrzeznaczenie = ({ onAddSuccess }) => {
  const toast = useRef(null);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const { user, token } = useContext(AuthContext);
  const [nazwa, setNazwa] = useState("");
  const [przeznaczenia, setPrzeznaczenia] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const fetchPrzeznaczenia = () => {
    axios
      .get(`${baseURL}api/przeznaczenia`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => setPrzeznaczenia(response.data))
      .catch((error) =>
        console.error("Błąd pobierania przeznaczeń:", error)
      );
  };

  useEffect(() => {
    fetchPrzeznaczenia();
  }, [token]);

  // Dodawanie nowego przeznaczenia
  const handleSubmit = () => {
    axios
      .post(
        `${baseURL}api/przeznaczeniaAdd`,
        { nazwa },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((response) => {
        setNazwa("");
        setPrzeznaczenia([...przeznaczenia, response.data]);
        onAddSuccess && onAddSuccess();
        toast.current.show({
          severity: "success",
          summary: "Dodano przeznaczenie",
          life: 3000,
        });
      })
      .catch((error) => {
        const msg =
          error.response?.status === 409
            ? "Takie przeznaczenie już istnieje"
            : "Nie udało się dodać przeznaczenia";
        toast.current.show({
          severity: error.response?.status === 409 ? "warn" : "error",
          summary: "Błąd",
          detail: msg,
          life: 3000,
        });
      });
  };

  const handleToggleAvailability = (item) => {
    if (editing && editing.id === item.id) {
      setEditing({ ...editing, dostepny: !editing.dostepny });
    }
  };

  const handleEditClick = (item) => {
    setEditing({ ...item });
  };

  const handleSaveClick = () => {
    axios
      .put(`${baseURL}api/przeznaczenia/${editing.id}`, editing, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setPrzeznaczenia(
          przeznaczenia.map((p) => (p.id === editing.id ? response.data : p))
        );
        setEditing(null);
        toast.current.show({
          severity: "success",
          summary: "Zapisano zmiany",
          life: 3000,
        });
      })
      .catch((error) => {
        const msg =
          error.response?.status === 409
            ? "Takie przeznaczenie już istnieje"
            : "Błąd aktualizacji przeznaczenia";
        toast.current.show({
          severity: error.response?.status === 409 ? "warn" : "error",
          summary: "Błąd",
          detail: msg,
          life: 3000,
        });
      });
  };

  const handleCancelClick = () => {
    setEditing(null);
    toast.current.show({
      severity: "info",
      summary: "Anulowano edycję",
      life: 3000,
    });
  };

  const textBodyTemplate = (rowData, field) => {
    if (editing && editing.id === rowData.id) {
      return (
        <InputText
          value={editing[field] || ""}
          onChange={(e) =>
            setEditing({ ...editing, [field]: e.target.value })
          }
        />
      );
    }
    return rowData[field];
  };

  const availabilityBodyTemplate = (rowData) => {
    if (editing && editing.id === rowData.id) {
      return (
        <Checkbox
          checked={editing.dostepny}
          onChange={() => handleToggleAvailability(rowData)}
        />
      );
    }
    return <Checkbox checked={rowData.dostepny} disabled />;
  };

  const actionBodyTemplate = (rowData) => {
    if (editing && editing.id === rowData.id) {
      return (
        <React.Fragment>
          <Button
            icon="pi pi-save"
            className="p-button-success p-mr-2"
            onClick={handleSaveClick}
          />
          <Button
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={handleCancelClick}
          />
        </React.Fragment>
      );
    }
    return (
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        onClick={() => handleEditClick(rowData)}
      />
    );
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="add-form">
        <h3>Dodaj nowe przeznaczenie</h3>
        <div className="add-input">
          <label>Nazwa:</label>
          <InputText value={nazwa} onChange={(e) => setNazwa(e.target.value)} />
        </div>
        <Button
          label="Dodaj przeznaczenie"
          onClick={handleSubmit}
          disabled={!nazwa.trim()}
        />
      </div>
      <Button
        label={showAll ? "Pokaż tylko dostępne" : "Pokaż wszystkie"}
        onClick={() => setShowAll(!showAll)}
        style={{ margin: "1rem 0" }}
      />
      {isMobile && (
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <i>
            W celu uzyskania lepszej widoczności danych, skorzystaj z orientacji
            poziomej
          </i>
        </div>
      )}
      <DataTable
        value={showAll ? przeznaczenia : przeznaczenia.filter((p) => p.dostepny)}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 15]}
        selectionMode="single"
        style={{ marginTop: "30px" }}
      >
        <Column
          field="nazwa"
          header="Nazwa"
          sortable
          body={(rowData) => textBodyTemplate(rowData, "nazwa")}
        />
        <Column
          header="Dostępny"
          field="dostepny"
          sortable
          body={availabilityBodyTemplate}
          headerStyle={{ textAlign: "center", width: "7rem" }}
          bodyStyle={{ textAlign: "center" }}
        />
        {user?.role === "Administrator" && (
          <Column header="Akcje" body={actionBodyTemplate} />
        )}
      </DataTable>
    </div>
  );
};

export default AddPrzeznaczenie;
